'use client'

import { forwardRef, useId } from 'react'

import { Combobox as BaseCombobox } from '@base-ui/react/combobox'
import { LuChevronDown } from 'react-icons/lu'

import { cn } from '@/core/utils/common'
import { useHydrated } from '@/core/hooks/use-hydrated'

import ComboboxDropdown from './combobox/combobox-dropdown'
import ComboboxSelect from './combobox/combobox-select'
import { choiceStyles } from './combobox/combobox.styles'
import { fieldStyles } from './field-styles'
import type { ComboboxOption, ComboboxProps } from './combobox/combobox.types'

const SearchableCombobox = forwardRef<HTMLButtonElement, ComboboxProps>(
  (
    {
      options,
      value,
      onChange,
      placeholder = '선택하세요',
      id: providedId,
      name,
      required,
      ariaLabel,
      helperText,
      searchPlaceholder,
      emptyMessage,
      label,
      error,
      disabled,
      className,
    },
    ref
  ) => {
    const id = useId()
    const hydrated = useHydrated()
    const triggerId = providedId ?? `${id}-trigger`
    const errorId = error ? `${id}-error` : undefined
    const helperId = helperText && !error ? `${id}-helper` : undefined
    const selected = options.find((option) => option.value === value) ?? null

    const handleValueChange = (option: ComboboxOption | null) => {
      if (option) {
        onChange?.(option.value)
      }
    }

    return (
      <BaseCombobox.Root
        items={options}
        name={name}
        required={required}
        itemToStringValue={(option) => option.value}
        value={selected}
        disabled={disabled || !hydrated}
        autoHighlight
        isItemEqualToValue={(option, selectedOption) =>
          option.value === selectedOption.value
        }
        onValueChange={handleValueChange}
      >
        <div className={cn('relative', className)}>
          {label && (
            <BaseCombobox.Label className={fieldStyles.label}>
              {label}
            </BaseCombobox.Label>
          )}

          <BaseCombobox.Trigger
            id={triggerId}
            ref={ref}
            aria-label={label ? undefined : (ariaLabel ?? placeholder)}
            aria-invalid={Boolean(error)}
            aria-describedby={errorId ?? helperId}
            className={cn(
              choiceStyles.trigger,
              error && 'border-error data-[popup-open]:border-error'
            )}
          >
            <BaseCombobox.Value placeholder={placeholder}>
              {(option: ComboboxOption | null) => (
                <span
                  className={cn(
                    'min-w-0 flex-1 truncate',
                    !option && 'text-description'
                  )}
                >
                  {option?.label ?? placeholder}
                </span>
              )}
            </BaseCombobox.Value>
            <BaseCombobox.Icon className="ml-auto shrink-0 text-description">
              <LuChevronDown
                aria-hidden="true"
                className="h-4 w-4 transition-transform duration-200 group-data-[popup-open]:rotate-180"
              />
            </BaseCombobox.Icon>
          </BaseCombobox.Trigger>

          <ComboboxDropdown
            label={label}
            errorId={errorId ?? helperId}
            invalid={Boolean(error)}
            searchPlaceholder={searchPlaceholder}
            emptyMessage={emptyMessage}
          />

          {error && (
            <p id={errorId} role="alert" className={fieldStyles.error}>
              {error}
            </p>
          )}
          {helperId && (
            <p id={helperId} className={fieldStyles.helper}>
              {helperText}
            </p>
          )}
        </div>
      </BaseCombobox.Root>
    )
  }
)

SearchableCombobox.displayName = 'SearchableCombobox'

export const Combobox = forwardRef<HTMLButtonElement, ComboboxProps>(
  (props, ref) =>
    props.searchable ? (
      <SearchableCombobox {...props} ref={ref} />
    ) : (
      <ComboboxSelect {...props} ref={ref} />
    )
)

Combobox.displayName = 'Combobox'
